import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSnackbarHandler } from 'web-library';
import { useTranslation } from 'react-i18next';
import { candidateService } from '../api';
import {
  GET_CANDIDATE_REGISTERED_SUBJECT,
  GET_EXAM_REGISTRATION,
  GET_SELECTED_SUBJECTS,
} from '../api/candidate/exam/@candidate/queries-services/keys';
import { EditPaperSubjectModal } from './PaperSubjectModal';
import { UpdateSubjectRegistrationDraftRequest } from '../api/candidate/exam/model';
import { mutateEditPaperSubject } from '../utils/ApiUtils';
import { useGetCandidateRegisteredSubject } from '../api/candidate/exam/@candidate/queries-services/queries';
import { setSubjectDetails } from '../utils/StringUtils';

export interface IEditStateHandler {
  openEditModal: Record<string, boolean>;
  setOpenEditModal: (arg: any) => void;
}

const EditExamSubjectsModal = (props: {
  applicationUuid: string;
  examRegistrationDetailUuid: string;
  editModalHandler: IEditStateHandler;
}) => {
  const { applicationUuid, examRegistrationDetailUuid, editModalHandler } = props;
  const { snackbarHandler } = useSnackbarHandler();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [paperState, setPaperState] = useState({});
  const paperStateHandling = { paperState, setPaperState };
  const { openEditModal, setOpenEditModal } = editModalHandler;
  const onEditModalCloseHandler = (uuid: string) => {
    setOpenEditModal(() => ({
      ...openEditModal,
      [uuid]: false,
    }));
    editSelectedSubjectReset();
  };

  const {
    data: registeredSubject,
    status: registeredSubjectStatus,
  } = useGetCandidateRegisteredSubject(applicationUuid, examRegistrationDetailUuid);

  useEffect(() => {
    if (registeredSubjectStatus === 'success' && registeredSubject) {
      const initialRegisteredSubjectState = {
        applicationUuid,
        examRegistrationDetailUuid,
        examSeriesUuid: registeredSubject?.examSeries?.find(
          (examSeries) => examSeries.selected === true
        )?.code,
        optionalPapers: registeredSubject.optionalPapers.map((optPaper) => ({
          uuid: optPaper.uuid,
          selected: optPaper.selected,
        })),
        callbackVariable: {
          subjectCode: registeredSubject.subjectCode,
          subjectName: registeredSubject.subjectName,
          subjectLevel: registeredSubject.subjectLevel,
        },
      };
      setPaperState((prevState: UpdateSubjectRegistrationDraftRequest) => ({
        ...prevState,
        ...initialRegisteredSubjectState,
      }));
    }
  }, [registeredSubjectStatus, applicationUuid, examRegistrationDetailUuid, registeredSubject]);

  const {
    mutate: editSelectedSubjectMutate,
    status: editSelectedSubjectStatus,
    reset: editSelectedSubjectReset,
  } = candidateService.exam.candidate.usePutExamSubjects(
    (res, variable) => {
      queryClient.invalidateQueries(GET_EXAM_REGISTRATION);
      queryClient.invalidateQueries([GET_SELECTED_SUBJECTS, applicationUuid]);
      queryClient.invalidateQueries([
        GET_CANDIDATE_REGISTERED_SUBJECT,
        variable.examRegistrationDetailUuid,
      ]);
      onEditModalCloseHandler(variable.examRegistrationDetailUuid);
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>SubjectSnackbar.HasBeenUpdated', {
          subjectDetails: setSubjectDetails(variable.callbackVariable),
        }),
      });
    },
    (err, variable) => {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'error',
        targetData: t('exam->>SubjectSnackbar.UnableToUpdate', {
          subjectDetails: setSubjectDetails(variable.callbackVariable),
        }),
        targetAnchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
    }
  );

  return (
    <>
      <EditPaperSubjectModal
        mutateSubjectHandler={{
          mutateSubjectStatus: editSelectedSubjectStatus,
        }}
        paperStateHandling={paperStateHandling}
        registeredSubjectHandling={{ registeredSubject, registeredSubjectStatus }}
        applicationUuid={applicationUuid}
        onClose={() => onEditModalCloseHandler(examRegistrationDetailUuid)}
        modelOpen={openEditModal[examRegistrationDetailUuid]}
        mutateChoosePaperSubject={(payload: UpdateSubjectRegistrationDraftRequest) =>
          mutateEditPaperSubject(editSelectedSubjectMutate, payload)
        }
      />
    </>
  );
};

export default EditExamSubjectsModal;
